import { useEffect, useRef, useState } from 'react';
import { get, isEmpty, isEqual } from 'lodash';
import { nativeEventType } from '../containers/Rewards/config';

/**
 * Hook implementation to mimic update lifecycle
 *
 * @param {Function} callback
 * @param {*} deps dependencies
 */
export const useDidUpdateEffect = (callback, deps) => {
  const hasMount = useRef(false);

  useEffect(() => {
    if (hasMount.current) {
      callback();
    } else {
      hasMount.current = true;
    }
  }, deps);
};

/**
 * Hook implementation to mimic update lifecycle
 *
 * @param {Function} callback
 * @param {*} deps dependencies
 */
export const useTheme = (elementId, themeData) => {
  const [isApplied, setIsApplied] = useState(false);
  const variables = [
    { name: '--default-font-family', value: 'fontFamilyName', defaultValue: 'Open Sans' },
    { name: '--default-font-size', value: 'defaultFontSize', defaultValue: '14px' },
    { name: '--default-font-weight', value: 'defaultFontWeight', defaultValue: 400 },
    { name: '--semi-bold-font-weight', value: 'semiBoldFontweight', defaultValue: 600 },
    { name: '--bold-font-weight', value: 'boldFontWeight', defaultValue: 700 },
    { name: '--primary-theme-color', value: 'primaryThemeColor', defaultValue: '#d08e46' },
    { name: '--secondary-theme-color', value: 'secondaryThemeColor', defaultValue: '#005BDD' },
    {
      name: '--primary-theme-light-color',
      value: 'primaryThemeLightColor',
      defaultValue: '#f7ebde',
    },
    { name: '--primary-bg-color', value: 'primaryBGColor', defaultValue: '#ffffff' },
    { name: '--secondary-bg-color', value: 'secondaryBGColor', defaultValue: '#fafafa' },
    { name: '--tertiary-bg-color', value: 'tertiaryBGColor', defaultValue: '#faf4ec' },
    { name: '--quaternary-bg-color', value: 'quaternaryBGColor', defaultValue: '#ffecd3' },
    { name: '--quinary-bg-color', value: 'quaternaryBGColor', defaultValue: '#1e1e1e' },
    { name: '--subtle-bg-color', value: 'subtleBGColor', defaultValue: '#F0F0F0' },
    { name: '--placeholder-bg-color', value: 'placeHolderBGColor', defaultValue: '#d9d9d9' },
    { name: '--button-bg-color', value: 'buttonBGColor', defaultValue: '#9c9c9c' },
    { name: '--disabled-bg-color', value: 'disabledBGColor', defaultValue: '#bdbdbd' },
    {
      name: '--current-level-tag-bg-color',
      value: 'currentLevelTagBGColor',
      defaultValue: '#682020',
    },
    { name: '--progress-unfilled-color', value: 'progressUnfilledColor', defaultValue: '#9c9c9c' },
    {
      name: '--card-CTA-bg-color',
      value: 'cardCTABGColor',
      defaultValue: 'rgba(255, 255, 255, 0.3)',
    },
    { name: '--primary-text-color', value: 'primaryTextColor', defaultValue: '#1e1e1e' },
    { name: '--secondary-text-color', value: 'secondaryTextColor', defaultValue: '#ffffff' },
    { name: '--tertiary-text-color', value: 'tertiaryTextColor', defaultValue: '#7c7c7c' },
    {
      name: '--quaternary-text-color',
      value: 'tertiaryTextColor',
      defaultValue: 'rgba(255, 255, 255, 0.6)',
    },
    { name: '--link-text-color', value: 'linkTextColor', defaultValue: '#4a90e2' },
    { name: '--border-color', value: 'borderColor', defaultValue: '#9c9c9c' },
    { name: '--secondary-border-color', value: 'secondaryBorderColor', defaultValue: '#e0e0e0' },
    { name: '--divider-color', value: 'dividerColor', defaultValue: '#dddddd' },
    { name: '--danger-color', value: 'dangerColor', defaultValue: '#d03131' },
    { name: '--success-color', value: 'successColor', defaultValue: '#37d3ae' },
    { name: '--warning-bg-color', value: 'warningBGColor', defaultValue: '#fff8ed' },
    { name: '--dash-border-color', value: 'dashBorderColor', defaultValue: '#000000' },
    { name: '--splash-text-color', value: 'splashTextColor', defaultValue: '#ffffff' },
    {
      name: '--box-shadow-color',
      value: 'boxShadowColor',
      defaultValue: 'rgba(0, 0, 0, 0.25)',
    },
    {
      name: '--card-box-shadow-color',
      value: 'cardBoxShadowColor',
      defaultValue: 'rgba(12, 27, 45, 0.3)',
    },
    { name: '--deal-default-bg-color', value: 'dealDefaultBGColor', defaultValue: '#f2c94c' },
    {
      name: '--secondary-box-shadow-color',
      value: 'secondaryBoxShadowColor',
      defaultValue: 'rgba(0, 0, 0, 0.1)',
    },
    { name: '--delivered-color', value: 'deliveredColor', defaultValue: '#3c3c3c' },
    { name: '--confirmed-color', value: 'confirmedColor', defaultValue: '#6ab11c' },
    { name: '--beta-tag-color', value: 'betaTagColor', defaultValue: '#2f80ed' },
    { name: '--warning-color', value: 'warningColor', defaultValue: '#d0021b' },
    { name: '--txn-success-color', value: 'txnSuccessColor', defaultValue: '#32B04E' },
    {
      name: '--txn-success-light-color',
      value: 'txnSuccessLightColor',
      defaultValue: 'rgba(50, 176, 78, 0.05)',
    },
    { name: '--txn-initiated-color', value: 'txnInitiatedColor', defaultValue: '#F2994A' },
    {
      name: '--txn-initiated-light-color',
      value: 'txnInitiatedLightColor',
      defaultValue: '#FFF5EB',
    },
    { name: '--danger-bg-color', value: 'dangerBgColor', defaultValue: '#FFEFEF' },
    { name: '--tier-card-shadow-color', value: 'tierCardShadowColor', defaultValue: '#00000040' },
    {
      name: '--card-linear-gradient',
      value: 'cardLinearGradient',
      defaultValue: 'linear-gradient(104.44deg, #B77930 -13.79%, #E19F1E 18.53%, #A75509 87.4%)',
    },
    {
      name: '--spinner-radial-gradient',
      value: 'spinnerRadialGradient',
      defaultValue:
        'radial-gradient(farthest-side, #d08e46 94%, #0000) top/12px 12px no-repeat, conic-gradient(#0000 30%, #d08e46)',
    },
    {
      name: '--spinner-mask-radial-gradient',
      value: 'spinnerMaskRadialGradient',
      defaultValue: 'radial-gradient(farthest-side, #0000 calc(100% - 12px), #000 0)',
    },
    {
      name: '--button-card-shadow-color',
      value: 'buttonCardShadowColor',
      defaultValue: '#0000001a',
    },
    {
      name: '--loyalty-card-mini-text-color',
      value: 'loyaltyCardMiniTextColor',
      defaultValue: '#ffffff',
    },
    {
      name: '--card-CTA-black-bg-color',
      value: 'cardCTABlackBGColor',
      defaultValue: 'rgba(0, 0, 0, 0.3)',
    },
  ];

  useEffect(() => {
    if (themeData) {
      const element = document.getElementById(elementId);
      if (element) {
        variables.forEach((variable) => {
          element.style.setProperty(
            variable.name,
            get(themeData, `${variable.value}`, variable.defaultValue),
          );
        });
      }
      setIsApplied(true);
    }
  }, [themeData]);

  return isApplied;
};

export const useEventListener = ({
  eventName,
  handler,
  element = window,
  backActionHandler,
  additionalEventHandler,
}) => {
  const handleEventAction = (msg) => {
    let dataFromApp = '';
    dataFromApp = get(msg, 'data', '');

    if (!isEmpty(dataFromApp)) {
      try {
        dataFromApp = dataFromApp && JSON.parse(dataFromApp);
      } catch {
        dataFromApp = {};
      }
      const eventType = get(dataFromApp, 'eventType');
      if (isEqual(eventType, nativeEventType.nativeBackPress)) {
        backActionHandler && backActionHandler();
      }
      additionalEventHandler && additionalEventHandler(dataFromApp);
    }
  };

  const eventHandler = isEmpty(handler) ? handleEventAction : handler;

  useEffect(() => {
    // Make sure element supports addEventListener
    const isSupported = element && element.addEventListener;
    if (!isSupported) return;

    // Add event listener
    element.addEventListener(eventName, eventHandler);

    // Remove event listener on cleanup
    return () => {
      element.removeEventListener(eventName, eventHandler);
    };
  }, [eventName, handler, element]);
};
